import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { ANONYM_USER, IS_MOBILE_BROWSER, IS_REGULAR_USER, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { businessTariffsPromoQuota } from 'reactApp/appHelpers/featuresHelpers/features/businessTariffsPromoQuota';
import { closeLimit, PROMO_ID, showInterval, showLimit } from 'reactApp/appHelpers/featuresHelpers/features/forWorkQuotaLandingPromo';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { store as reduxStore } from 'reactApp/store';
import { sendXray } from 'reactApp/utils/ga';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/ForWorkQuotaLandingPromo/ForWorkQuotaLandingPromo'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const forWorkQuotaLandingPromoCreator: ModelCreator = ({ models, render, stores }) =>
    new PopupModel(
        PROMO_ID,
        models.base,
        new BaseModelProps(PROMO_ID, {
            store: new LocalStorageStore(PROMO_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_ID, SuspendedComponent, render.react),
            closable: true,
            showLimit,
            closeLimit,
            showInterval,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: true,
            hideOnCompleted: true,
            hideOnSnooze: true,
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);
                if (storage !== EStorageType.quotaCleaner) {
                    reject();
                    return;
                }

                const { action = '' } = SettingsSelectors.getQueryParams(state);
                if (action) {
                    reject();
                    sendXray(['promo-test', PROMO_ID, 'rejectbyaction']);
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isOverquota = isCloudOrMailOrApiOverQuota(state);
                const isPaidUser = UserSelectors.isPaidUser(state);

                const isReject =
                    isPaidUser ||
                    !businessTariffsPromoQuota.enabled ||
                    IS_WEBVIEW ||
                    IS_MOBILE_BROWSER ||
                    !(IS_REGULAR_USER || ANONYM_USER) ||
                    isBizUser ||
                    isCorpUser ||
                    isOverquota;

                sendXray(['promo-test', PROMO_ID, isReject ? 'reject' : 'resolve']);

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
        })
    );
